import React, { createContext, useContext, useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useFilterContext } from './SharedFilterContext';
import { useAuth } from './AuthContext';
import WorkOrders from '../models/WorkOrders';
import Property from '../models/Property';
import moment from 'moment';
import pLimit from 'p-limit';
import {
  applyWorkOrderFiltering,
  getFilterHash as computeFilterHash,
  getPropertyListSize
} from '../utils/workOrderFiltering';

/**
 * MikeContext - Global state management for Mike AI Assistant
 *
 * Provides:
 * - Conversation state (messages, isOpen, etc.)
 * - Work order data and fetching (Meijer)
 * - Dashboard data state (non-Meijer, and Meijer on dashboard page)
 * - Page tracking for Meijer hybrid mode (dashboard + work orders)
 * - Download and clear conversation features
 * - Date range and page change detection
 */

/**
 * Build a human-readable work order summary block for a date range.
 * Mirrors the summary shown on first open so the date-change follow-up
 * message stays consistent with the welcome summary.
 */
const buildWorkOrderSummaryText = (workOrders, start, end, title = 'Work Order Summary (Updated Date Range)') => {
  const wos = workOrders || [];
  const total = wos.length;
  const compressorFailures = wos.filter(wo => wo.compressor_failure_detected).length;
  const statusCounts = wos.reduce((acc, wo) => {
    const status = wo.status_id || 'Unknown';
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});
  const totalCost = wos.reduce((sum, wo) => sum + (parseFloat(wo.costs_total_value) || 0), 0);
  const statusLines = Object.entries(statusCounts)
    .map(([status, count]) => `  - ${status}: ${count}`)
    .join('\n');

  return `${title}
━━━━━━━━━━━━━━━━━━━━━━━━
• Date Range: ${moment(start).format('MMM D, YYYY')} to ${moment(end).format('MMM D, YYYY')}
• Total Work Orders: ${total}
• Compressor Failures: ${compressorFailures}${compressorFailures > 0 ? ' (ACTION REQUIRED)' : ''}
• Status Breakdown:
${statusLines || '  - N/A'}
• Total Costs: $${totalCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const MikeContext = createContext(null);

export const useMike = () => {
  const context = useContext(MikeContext);
  if (!context) {
    throw new Error('useMike must be used within MikeProvider');
  }
  return context;
};

export const MikeProvider = ({ children }) => {
  const { user } = useAuth();
  const {
    startDate,
    endDate,
    favoritedPropertyIds,
    availablePropertyLists,
    inspectionViewMode,
    selectedPropertyList
  } = useFilterContext();

  // Conversation state
  const [messages, setMessages] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [hasShownWelcome, setHasShownWelcome] = useState(false);
  const [hasShownDisclaimer, setHasShownDisclaimer] = useState(false);
  const [error, setError] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);

  // Page tracking for Meijer hybrid mode (dashboard + work orders)
  const [currentPage, setCurrentPage] = useState(null);

  // Work order state (Meijer)
  const [workOrders, setWorkOrders] = useState([]);
  const [loadingWorkOrders, setLoadingWorkOrders] = useState(false);
  const [workOrdersFetched, setWorkOrdersFetched] = useState(false);

  // Dashboard data state (non-Meijer companies)
  // Each card type includes enrichedDetails for Mike's detailed context
  const [dashboardData, setDashboardData] = useState({
    activeUsers: null,        // { byRole: [], details: [], enrichedDetails: [] }
    msiUsed: null,            // { count: number, totalDevices: number, details: [], enrichedDetails: [], zohoDevices: [] }
    propertiesInspected: null, // { count: number, total: number, details: [], enrichedDetails: [] }
    criticalInspections: null, // { redPoints: number, evaluations: number, pointInfos: [], vals: [], figsEvaluations: [], criticalPointsDetails: [], criticalEvalsDetails: [] }
    reports: null,            // { count: number, details: [], enrichedDetails: [] }
    taskingSheets: null,      // { count: number, total: number, details: [] }
    allCompanyProperties: null // { total: number, properties: [] }
  });
  const [dashboardDataFetched, setDashboardDataFetched] = useState({
    activeUsers: false,
    msiUsed: false,
    propertiesInspected: false,
    criticalInspections: false,
    reports: false,
    taskingSheets: false,
    allCompanyProperties: false
  });

  // Favorite property names (for non-Meijer context)
  const [favoritePropertyNames, setFavoritePropertyNames] = useState([]);

  // Derived helpers for company and page detection
  const isMeijer = user?.company?.id === "1121";
  const isOnDashboard = currentPage === '/company_admin_dashboard' || currentPage === '/company_admin_dashboard/';

  // Mike's filtered work order view — mirrors the same property_uuid filtering
  // that WorkOrdersScreen applies, so Mike answers about exactly the set the
  // user sees in the table.
  const filteredWorkOrders = useMemo(
    () => applyWorkOrderFiltering(workOrders, {
      viewMode: inspectionViewMode,
      selectedPropertyList,
      favoritedPropertyIds
    }),
    [workOrders, inspectionViewMode, selectedPropertyList, favoritedPropertyIds]
  );

  // Track date range changes
  const previousDateRangeHash = useRef(null);
  // Track shared-filter changes (view mode / property group / favorites)
  const previousFilterHash = useRef(null);
  // Set true while the date-range effect is in-flight so the filter effect
  // doesn't double-announce when date+filter change in the same render.
  const pendingDateRangeUpdate = useRef(false);

  // Work order fetching constants
  const CHUNK_SIZE = 100;
  const MAX_CONCURRENCY = 6;
  const workOrdersLimit = pLimit(MAX_CONCURRENCY);

  /**
   * Fetch chunk of work orders by IDs
   */
  const fetchWorkOrdersChunk = async (ids) => {
    const searchList = [{ field: 'work_order_id', value: ids, comparison: 'IN' }];
    const { data } = await WorkOrders.filterBy(
      searchList,
      'dt_created',
      'desc',
      10000,
      undefined,
      undefined,
      undefined,
      true
    );
    return data;
  };

  /**
   * Fetch all work orders in date range
   */
  const fetchWorkOrdersByDateRange = useCallback(async () => {
    if (user?.company?.id !== "1121") {
      console.log('[Mike][Context] Not Meijer company, skipping work order fetch');
      setWorkOrders([]);
      setLoadingWorkOrders(false);
      return [];
    }

    console.log('[Mike][Context] Fetching work orders by date range');
    setLoadingWorkOrders(true);

    try {
      // Get work order IDs by date range
      const workOrderIds = await WorkOrders.getWorkOrderIdsByDateRange(
        moment(startDate).format('YYYY-MM-DD HH:mm:ss'),
        moment(endDate).add(1, 'day').format('YYYY-MM-DD HH:mm:ss')
      );

      console.log('[Mike][Context] Work Order IDs by Date Range:', workOrderIds.length);

      // Break work order IDs into chunks
      const chunks = [];
      for (let i = 0; i < workOrderIds.length; i += CHUNK_SIZE) {
        chunks.push(workOrderIds.slice(i, i + CHUNK_SIZE));
      }

      // Fetch work orders for each chunk in parallel
      let allWorkOrders = [];
      await Promise.all(
        chunks.map((chunk) =>
          workOrdersLimit(() => fetchWorkOrdersChunk(chunk).then((res) => allWorkOrders.push(...res)))
        )
      );

      console.log('[Mike][Context] Total work orders fetched:', allWorkOrders.length);
      setWorkOrders(allWorkOrders);
      setWorkOrdersFetched(true);
      return allWorkOrders;
    } catch (err) {
      console.error('[Mike][Context] Error fetching work orders:', err);
      setWorkOrders([]);
      setError('Failed to fetch work orders');
      return null;
    } finally {
      setLoadingWorkOrders(false);
    }
  }, [startDate, endDate, user?.company?.id]);

  /**
   * Update dashboard data from CompanyAdminDashboard
   * Used by non-Meijer companies and Meijer when on dashboard page
   */
  const updateDashboardData = useCallback((cardType, data) => {
    console.log('[Mike][Context] Updating dashboard data:', cardType);
    setDashboardData(prev => ({ ...prev, [cardType]: data }));
    setDashboardDataFetched(prev => ({ ...prev, [cardType]: true }));
  }, []);

  /**
   * Reset dashboard data (when filters change)
   */
  const resetDashboardData = useCallback(() => {
    console.log('[Mike][Context] Resetting dashboard data');
    setDashboardData({
      activeUsers: null,
      msiUsed: null,
      propertiesInspected: null,
      criticalInspections: null,
      reports: null,
      taskingSheets: null,
      allCompanyProperties: null
    });
    setDashboardDataFetched({
      activeUsers: false,
      msiUsed: false,
      propertiesInspected: false,
      criticalInspections: false,
      reports: false,
      taskingSheets: false,
      allCompanyProperties: false
    });
  }, []);

  /**
   * Fetch favorite property names when favoritedPropertyIds changes
   */
  useEffect(() => {
    const fetchFavoriteNames = async () => {
      if (!favoritedPropertyIds || favoritedPropertyIds.length === 0) {
        setFavoritePropertyNames([]);
        return;
      }

      try {
        // Fetch property names by UUIDs
        const properties = await Property.getPropertyNamesByUuids(favoritedPropertyIds);
        const names = properties?.map(p => p.name) || [];
        setFavoritePropertyNames(names);
        console.log('[Mike][Context] Fetched favorite property names:', names.length);
      } catch (error) {
        console.error('[Mike][Context] Error fetching favorite property names:', error);
        setFavoritePropertyNames([]);
      }
    };

    fetchFavoriteNames();
  }, [favoritedPropertyIds]);

  /**
   * Get current date range hash for change detection
   */
  const getDateRangeHash = useCallback(() => {
    return JSON.stringify({
      startDate: moment(startDate).format('YYYY-MM-DD'),
      endDate: moment(endDate).format('YYYY-MM-DD')
    });
  }, [startDate, endDate]);

  /**
   * Detect date range changes and update context.
   *
   * Hash only advances after the change has been handled (Mike was open).
   * If Mike is closed when the date range changes, previousHash is left stale
   * so the diff is re-detected the next time isOpen flips true — otherwise the
   * change would be silently consumed and Mike would keep serving stale work
   * orders against new dates.
   */
  useEffect(() => {
    const currentHash = getDateRangeHash();

    // Initial mount: set baseline, no notification
    if (previousDateRangeHash.current === null) {
      previousDateRangeHash.current = currentHash;
      return;
    }

    // No change
    if (previousDateRangeHash.current === currentHash) return;

    // Date range changed — only handle when Mike is open. If closed, leave
    // previousHash stale so the diff is picked up next time isOpen flips true.
    if (!isOpen) return;

    console.log('[Mike][Context] Date range changed, updating context');

    if (isMeijer) {
      // Meijer: always refresh work orders
      const messageText = isOnDashboard
        ? `Context Updated!\n\nThe date range has changed. I'm refreshing the work order data for the new date range. Dashboard card data will also need to be refreshed.\n\nNew Date Range: ${moment(startDate).format('MMM D, YYYY')} to ${moment(endDate).format('MMM D, YYYY')}\n\nAll data references from my previous answers are now outdated. Please re-ask any questions you need answered with the updated data.`
        : `Context Updated!\n\nThe date range has changed. I'm refreshing the work order data for the new date range.\n\nNew Date Range: ${moment(startDate).format('MMM D, YYYY')} to ${moment(endDate).format('MMM D, YYYY')}\n\nAll data references from my previous answers are now outdated. Please re-ask any questions you need answered with the updated data.`;
      const updateMessage = {
        id: Date.now(),
        sender: 'bot',
        text: messageText,
        timestamp: new Date(),
        isUpdate: true
      };
      setMessages(prev => [...prev, updateMessage]);

      // Capture the new range AND the active filter state now so the follow-up
      // summary matches the snapshot this update announced, even if the user
      // changes dates or filters again mid-fetch.
      const summaryStart = startDate;
      const summaryEnd = endDate;
      const summaryFilterSnapshot = {
        inspectionViewMode,
        selectedPropertyList,
        favoritedPropertyIds
      };

      // Mark the date-range update as in-flight so the filter effect (which
      // runs immediately after this body in the same React effect flush)
      // doesn't double-announce when both dimensions changed at once.
      pendingDateRangeUpdate.current = true;

      fetchWorkOrdersByDateRange().then((freshOrders) => {
        // Apply the snapshotted filter so the summary reflects Mike's actual
        // filtered context, not the raw fetched set.
        const freshFiltered = freshOrders === null
          ? null
          : applyWorkOrderFiltering(freshOrders, {
              viewMode: summaryFilterSnapshot.inspectionViewMode,
              selectedPropertyList: summaryFilterSnapshot.selectedPropertyList,
              favoritedPropertyIds: summaryFilterSnapshot.favoritedPropertyIds
            });

        const summaryMessage = {
          id: Date.now(),
          sender: 'bot',
          text: freshOrders === null
            ? `I couldn't load the work orders for the new date range. Please change the date range again or refresh the page to retry.`
            : buildWorkOrderSummaryText(freshFiltered, summaryStart, summaryEnd),
          timestamp: new Date(),
          isUpdate: true
        };
        setMessages(prev => [...prev, summaryMessage]);

        // Consume the snapshotted filter into the filter hash so the filter
        // effect treats this filter state as already-announced. If the user
        // changed the filter mid-fetch, that diff will be picked up on the
        // next render via the filter effect.
        previousFilterHash.current = computeFilterHash(summaryFilterSnapshot);
        pendingDateRangeUpdate.current = false;
      });

      if (isOnDashboard) {
        resetDashboardData();
      }
    } else {
      // Non-Meijer: reset dashboard data and notify user
      const updateMessage = {
        id: Date.now(),
        sender: 'bot',
        text: `Context Updated!\n\nThe date range changed to ${moment(startDate).format('MMM D, YYYY')} - ${moment(endDate).format('MMM D, YYYY')}.\n\nMy previous answers were based on the old date range and may no longer be accurate. Please click "Get Results" on the cards again to refresh the data, then re-ask your questions.`,
        timestamp: new Date(),
        isUpdate: true
      };
      setMessages(prev => [...prev, updateMessage]);
      resetDashboardData();
    }

    previousDateRangeHash.current = currentHash;
  }, [isOpen, startDate, endDate, getDateRangeHash, fetchWorkOrdersByDateRange, resetDashboardData, isMeijer, isOnDashboard, inspectionViewMode, selectedPropertyList, favoritedPropertyIds]);

  /**
   * Detect shared-filter changes (inspectionViewMode / selectedPropertyList /
   * favoritedPropertyIds) and notify the user.
   *
   * Mirrors the date-range effect's hash-then-announce pattern:
   *   - Hash advances only after the change has been handled (Mike was open).
   *   - If Mike is closed when the filter changes, hash is left stale so the
   *     diff is re-detected on next open (consumed silently by toggleChat so
   *     the welcome IS the announcement on first open).
   *   - If a date-range update is in flight (pendingDateRangeUpdate), defer to
   *     it — its .then() advances previousFilterHash so this effect stays
   *     silent for the same-render combo.
   *   - No refetch: the underlying fetched set hasn't changed, only the view.
   */
  useEffect(() => {
    const currentHash = computeFilterHash({
      inspectionViewMode,
      selectedPropertyList,
      favoritedPropertyIds
    });

    // Initial mount: set baseline, no notification
    if (previousFilterHash.current === null) {
      previousFilterHash.current = currentHash;
      return;
    }

    if (previousFilterHash.current === currentHash) return;

    // Mike closed → leave stale, picked up on next open (toggleChat will
    // consume silently so the welcome is the announcement).
    if (!isOpen) return;

    // Non-Meijer has no work orders in context — consume silently.
    if (!isMeijer) {
      previousFilterHash.current = currentHash;
      return;
    }

    // Date-range update in flight → let it own this turn. Its .then() will
    // advance previousFilterHash to the new state.
    if (pendingDateRangeUpdate.current) return;

    console.log('[Mike][Context] Filter changed, updating context');

    const filteredCount = filteredWorkOrders.length;
    let updateText;
    if (inspectionViewMode === 'favorited') {
      const favCount = (favoritedPropertyIds || []).length;
      const propWord = favCount === 1 ? 'property' : 'properties';
      updateText = `Context Updated!\n\nYour filter changed to **Favorited Properties**. I'm now focused on ${filteredCount} work orders across your ${favCount} favorited ${propWord}.`;
    } else if (inspectionViewMode === 'propertyList') {
      const listName = selectedPropertyList?.name || 'selected';
      const propCount = getPropertyListSize(selectedPropertyList);
      const propCountText = propCount === null ? 'the' : `${propCount}`;
      const propWord = propCount === 1 ? 'property' : 'properties';
      updateText = `Context Updated!\n\nYour filter changed to property group **${listName}**. I'm now focused on ${filteredCount} work orders across ${propCountText} ${propWord} in this group.`;
    } else {
      updateText = `Context Updated!\n\nYour filter changed to **All Work Orders**. I'm now looking at all ${filteredCount} work orders in the date range.`;
    }

    const updateMessage = {
      id: Date.now(),
      sender: 'bot',
      text: updateText,
      timestamp: new Date(),
      isUpdate: true
    };
    const summaryMessage = {
      id: Date.now() + 1,
      sender: 'bot',
      text: buildWorkOrderSummaryText(
        filteredWorkOrders,
        startDate,
        endDate,
        'Work Order Summary (Filter Changed)'
      ),
      timestamp: new Date(),
      isUpdate: true
    };
    setMessages(prev => [...prev, updateMessage, summaryMessage]);

    previousFilterHash.current = currentHash;
  }, [isOpen, isMeijer, inspectionViewMode, selectedPropertyList, favoritedPropertyIds, filteredWorkOrders, startDate, endDate]);

  // Track previous dashboardDataFetched state
  const previousDashboardDataFetched = useRef(null);

  /**
   * Detect dashboard data updates while Mike is open
   * Shows a simple "Context Updated" message when new data becomes available
   * For Meijer: only active when on the Company Admin Dashboard page
   */
  useEffect(() => {
    // For Meijer: only show dashboard data notifications when on dashboard page
    // For non-Meijer: always show (current behavior)
    if (isMeijer && !isOnDashboard) return;

    // Skip on initial mount
    if (previousDashboardDataFetched.current === null) {
      previousDashboardDataFetched.current = { ...dashboardDataFetched };
      return;
    }

    // Check if any card just became fetched (was false, now true)
    const newlyFetchedCards = Object.entries(dashboardDataFetched)
      .filter(([card, fetched]) => fetched && !previousDashboardDataFetched.current[card])
      .map(([card]) => card);

    // Update ref immediately for next comparison
    previousDashboardDataFetched.current = { ...dashboardDataFetched };

    // If Mike is open and new data was just fetched, show generic notification
    if (isOpen && newlyFetchedCards.length > 0 && messages.length > 0) {
      // Check if we already have a recent context update message (avoid spam)
      const recentUpdate = messages.slice(-3).some(m => m.isUpdate);
      if (recentUpdate) return;

      const updateMessage = {
        id: Date.now(),
        sender: 'bot',
        text: `Context Updated!\n\nI now have access to new dashboard data. Note: my previous answers did not include this data, so you may want to re-ask questions that depend on it.`,
        timestamp: new Date(),
        isUpdate: true
      };

      console.log('[Mike][Context] Dashboard data updated, notifying user');
      setMessages(prev => [...prev, updateMessage]);
    }
  }, [isOpen, dashboardDataFetched, messages.length, isMeijer, isOnDashboard]);

  // Track page changes for Meijer hybrid mode
  const previousPage = useRef(null);

  /**
   * Detect page changes for Meijer users
   * Shows "Context Updated" notification when navigating to/from Company Admin Dashboard
   */
  useEffect(() => {
    if (!isMeijer) return; // Only Meijer needs page change tracking

    // Skip initial mount
    if (previousPage.current === null) {
      previousPage.current = currentPage;
      return;
    }

    // Detect actual page change
    if (previousPage.current === currentPage) return;

    const wasOnDashboard = previousPage.current === '/company_admin_dashboard' || previousPage.current === '/company_admin_dashboard/';
    const nowOnDashboard = currentPage === '/company_admin_dashboard' || currentPage === '/company_admin_dashboard/';

    previousPage.current = currentPage;

    // Only notify if Mike is open AND there are existing messages
    if (!isOpen || messages.length === 0) return;

    // Check for recent context update message (avoid spam)
    const recentUpdate = messages.slice(-3).some(m => m.isUpdate);
    if (recentUpdate) return;

    if (!wasOnDashboard && nowOnDashboard) {
      // Navigated TO dashboard
      const updateMessage = {
        id: Date.now(),
        sender: 'bot',
        text: `Context Updated!\n\nYou're now on the Company Admin Dashboard. I have access to both your work order data and dashboard analytics.\n\nYou can ask me about work order summaries, compressor failures, active users, inspections, reports, or any dashboard metrics. What would you like to explore?`,
        timestamp: new Date(),
        isUpdate: true
      };
      setMessages(prev => [...prev, updateMessage]);
    } else if (wasOnDashboard && !nowOnDashboard) {
      // Navigated AWAY from dashboard
      const updateMessage = {
        id: Date.now(),
        sender: 'bot',
        text: `Context Updated!\n\nYou've navigated away from the dashboard. I no longer have access to dashboard analytics data. My previous answers about dashboard metrics no longer apply in this context.\n\nI'm now focused on work order analysis. Feel free to ask me about your work orders and compressor failures.`,
        timestamp: new Date(),
        isUpdate: true
      };
      setMessages(prev => [...prev, updateMessage]);
    }
  }, [currentPage, isOpen, messages, isMeijer]);

  /**
   * Toggle chat open/closed
   */
  const toggleChat = useCallback(() => {
    setIsOpen(prev => {
      const next = !prev;
      console.log('[Mike][Context] toggleChat():', next ? 'open' : 'minimized');

      // Fetch work orders on first open if not already fetched
      if (next && !workOrdersFetched && user?.company?.id === "1121") {
        console.log('[Mike][Context] First open, fetching work orders');
        setLoadingWorkOrders(true); // Set immediately so welcome message waits for data
        fetchWorkOrdersByDateRange();
        // Sync hashes so neither change-detection effect fires a duplicate
        // announcement against the welcome message itself. The welcome IS the
        // announcement on first open.
        previousDateRangeHash.current = getDateRangeHash();
        previousFilterHash.current = computeFilterHash({
          inspectionViewMode,
          selectedPropertyList,
          favoritedPropertyIds
        });
      }

      return next;
    });
  }, [workOrdersFetched, user?.company?.id, fetchWorkOrdersByDateRange, getDateRangeHash, inspectionViewMode, selectedPropertyList, favoritedPropertyIds]);

  /**
   * Toggle expanded/collapsed chat size
   */
  const toggleExpanded = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);

  /**
   * Clear conversation
   */
  const clearConversation = useCallback(() => {
    setMessages([]);
    setHasShownWelcome(false);
    setHasShownDisclaimer(false);
    setError(null);
    setIsOpen(false);  // Minimize chat back to floating icon
    setIsExpanded(false);  // Reset to normal size
    console.log('[Mike][Context] Conversation cleared');
  }, []);

  /**
   * Download conversation as text file
   */
  const downloadConversation = useCallback(() => {
    if (messages.length === 0) {
      console.log('[Mike][Context] No messages to download');
      return;
    }

    // Describe the active filter view for the export header
    const viewModeLabel = inspectionViewMode === 'favorited'
      ? 'Favorited Properties'
      : inspectionViewMode === 'propertyList'
        ? `Property Group: ${selectedPropertyList?.name || '(unnamed)'}`
        : 'All Work Orders';

    // Build conversation text
    const header = `Mike AI Conversation Export
Company: ${user?.company?.name || 'Meijer'}
User: ${user?.full_name || 'User'}
Date Range: ${moment(startDate).format('MMM D, YYYY')} to ${moment(endDate).format('MMM D, YYYY')}
Export Date: ${moment().format('MMMM D, YYYY [at] h:mm A')}
View Mode: ${viewModeLabel}
Total Work Orders (filtered): ${filteredWorkOrders.length}

================================

`;

    const conversationText = messages
      .map(msg => {
        const timestamp = moment(msg.timestamp).format('MMM D, YYYY h:mm A');
        const sender = msg.sender === 'user' ? 'User' : 'Mike';
        return `[${timestamp}] ${sender}:\n${msg.text}\n`;
      })
      .join('\n');

    const fullText = header + conversationText;

    // Create blob and download
    const blob = new Blob([fullText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `mike-conversation-${moment().format('YYYY-MM-DD-HHmmss')}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    console.log('[Mike][Context] Conversation downloaded');
  }, [messages, user, startDate, endDate, filteredWorkOrders.length, inspectionViewMode, selectedPropertyList]);

  const value = {
    // Conversation state
    messages,
    setMessages,
    isOpen,
    setIsOpen,
    hasShownWelcome,
    setHasShownWelcome,
    hasShownDisclaimer,
    setHasShownDisclaimer,
    error,
    setError,
    isExpanded,
    toggleExpanded,

    // Work order state (Meijer)
    workOrders,
    filteredWorkOrders,
    loadingWorkOrders,
    workOrdersFetched,

    // Dashboard data state (non-Meijer, and Meijer on dashboard)
    dashboardData,
    dashboardDataFetched,
    favoritePropertyNames,
    availablePropertyLists,

    // Page tracking (Meijer hybrid mode)
    currentPage,
    setCurrentPage,
    isOnDashboard,

    // Functions
    toggleChat,
    clearConversation,
    downloadConversation,
    fetchWorkOrdersByDateRange,
    updateDashboardData,
    resetDashboardData
  };

  return <MikeContext.Provider value={value}>{children}</MikeContext.Provider>;
};
